import MenuDropdown from "@Components/MenuDropdown";
import Badge from "@Pages/Search/components/FilterSection/components/Badge/Badge";
import { PROPERTY_FILTERS } from "@Pages/Search/components/FilterSection/data/filtersList";
import getFilterActiveStatus from "@Pages/Search/utils/getFilterActiveStatus";
import getFilterLabel, { FilterNameType } from "@Pages/Search/utils/getFilterLabel";
import { Fragment, Suspense, lazy } from "react";
import styles from "../PropertyFiltersSection.module.scss";
import FullPageLoader from "@Components/FullPageLoader";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import useTranslationHook from "hooks/useTranslationHook";

const ActiveFilterComponent = lazy(
  () =>
    import(
      "@Pages/Search/components/FilterSection/components/ActiveFilterComponent/ActiveFilterComponent"
    ),
);

type ActiveFilterType = (typeof PROPERTY_FILTERS)[number]["name"] | "";

type FilterBadgeGroup = {
  onFilterClick: (name?: ActiveFilterType) => void;
  onFilterClose: () => void;
  filters: any;
  isDesktop?: boolean;
  onApplyClick?: () => void;
  onClearClick?: () => void;
  dispatch?: any;
  filtersState?: any;
  activeFilter?: any;
  dynamiceFilterCount?: any;
  hasDualOccupancy?: boolean;
};

const FiltersBadgeGroup = ({
  onFilterClick,
  onFilterClose,
  filters,
  isDesktop = false,
  onClearClick,
  onApplyClick,
  dispatch,
  filtersState,
  activeFilter,
  dynamiceFilterCount,
  hasDualOccupancy,
}: FilterBadgeGroup) => {
  const { isWhitelabel } = useWhitelabel();
  const { t } = useTranslationHook();

  return (
    <>
      {PROPERTY_FILTERS.map(({ title, name, whitelabelTitle }, index) => {
        const isActive = getFilterActiveStatus(title as FilterNameType, filters, true);
        const activeLabel =
          isActive && getFilterLabel(title as FilterNameType, filters, undefined, undefined, t);

        // Hide Dual Occupancy badge if no room type has dual_occupancy tag
        if (name === "Dual Occupancy" && !hasDualOccupancy) {
          return null;
        }

        // Hide Stay Duration badge if no options returned from backend
        if (
          name === "Stay Duration" &&
          dynamiceFilterCount &&
          (!dynamiceFilterCount.data?.lease_duration ||
            Object.keys(dynamiceFilterCount.data.lease_duration).length === 0)
        ) {
          return null;
        }

        // Hide Move in Month badge if no options returned from backend
        if (
          name === "Move in Month" &&
          dynamiceFilterCount &&
          (!dynamiceFilterCount.data?.move_in_month ||
            Object.keys(dynamiceFilterCount.data.move_in_month).length === 0)
        ) {
          return null;
        }

        return (
          <Fragment key={`${title}_${index}`}>
            {isDesktop ? (
              <>
                <MenuDropdown
                  menu={
                    <Suspense fallback={<FullPageLoader height="230px" width="360px" />}>
                      <ActiveFilterComponent
                        filterName={title}
                        filtersState={filtersState}
                        dispatch={dispatch}
                        universityData={null}
                        localityData={null}
                        onApplyClick={onApplyClick}
                        onResetClick={onClearClick}
                        withResetButton
                        dynamiceFilterCount={dynamiceFilterCount}
                        restrictToAvailable
                      />
                    </Suspense>
                  }
                  target={
                    <Badge
                      key={`${title}_${index}`}
                      value={isWhitelabel ? whitelabelTitle : title}
                      activeLabel={activeLabel}
                      isActive={isActive}
                      invertColor={isActive}
                      onClick={() => {
                        if (name === activeFilter) {
                          onFilterClose();
                        } else {
                          onFilterClick(name);
                        }
                      }}
                      className={styles.badge}
                    />
                  }
                  direction="right"
                  menuContainerClassName={styles.badgeContainer}
                  forceClose={activeFilter !== "" && activeFilter !== name}
                  handleOnClose={() => {
                    onFilterClose();
                  }}
                  closeOnSelect={false}
                />
              </>
            ) : (
              <Badge
                key={`${title}_${index}`}
                value={isWhitelabel ? whitelabelTitle : title}
                activeLabel={activeLabel}
                isActive={isActive}
                onClick={() => onFilterClick(name)}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
};

export default wrapperHOC(FiltersBadgeGroup, {
  componentName: "FiltersBadgeGroup-PropertyFiltersSection-RoomTypeSection-PropertyPage",
  showForChina: true,
});
